import { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { Link } from "react-router-dom";

import useAuth from "../../hooks/useAuth";
import RejectionModal from "../../pages/RejectionModal";
import authService from "../../services/AuthService";
import SidebarItem from "./SidebarItem";

interface SidebarProps {
  className: string;
  isOpen?: boolean;
  onClose?: () => void;
}

export default function Sidebar({ className, isOpen, onClose }: SidebarProps) {
  const history = useHistory();

  const { setAuthenticatedUser } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userRoleId, setUserRoleId] = useState(0);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      const roleId = parsedUser.roleId;
      setUserRoleId(roleId);
    }
  }, []);

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleLogout = async () => {
    await authService.logout();
    setAuthenticatedUser(null);
    history.push("/login");
  };

  const profileSvgCode = `
  <svg xmlns="http://www.w3.org/2000/svg" class="svgIcon"  viewBox="0 0 36 36">
    <defs>
        <clipPath id="7f3agx481a">
            <path data-name="Rectangle 16" style="fill:#fff" d="M0 0h36v36H0z"/>
        </clipPath>
    </defs>
    <g data-name="Group 116">
        <g data-name="Group 3">
            <g data-name="Group 2" style="clip-path:url(#7f3agx481a)">
                <path data-name="Path 1" d="M23.636 19.313a9.565 9.565 0 1 0-11.27 0 14.455 14.455 0 0 0-8.8 13.268 1.5 1.5 0 0 0 1.5 1.5 1.5 1.5 0 0 0 1.5-1.5 11.434 11.434 0 0 1 22.867.014 1.5 1.5 0 1 0 3 0 14.456 14.456 0 0 0-8.8-13.287M11.435 11.6A6.565 6.565 0 1 1 18 18.166a6.572 6.572 0 0 1-6.565-6.566" style="fill:#fff"/>
            </g>
        </g>
        <path data-name="Rectangle 153" style="fill:none" d="M0 0h36v36H0z"/>
    </g>
  </svg>
  `;

  const dashboardSvgCode = `
  <svg xmlns="http://www.w3.org/2000/svg" class="svgIcon" viewBox="0 0 36 36">
    <g data-name="Group 115">
        <path fill=#fff  d="M183.2-765.814h7.139v-10.749a1.865 1.865 0 0 1 .554-1.374 1.866 1.866 0 0 1 1.374-.554h7.467a1.866 1.866 0 0 1 1.374.554 1.865 1.865 0 0 1 .554 1.374v10.749h7.138v-18.872a.619.619 0 0 0-.072-.3.8.8 0 0 0-.195-.236l-12.144-9.128a.573.573 0 0 0-.39-.144.573.573 0 0 0-.39.144l-12.144 9.128a.8.8 0 0 0-.195.236.619.619 0 0 0-.072.3zm-3.2 0v-18.872a3.828 3.828 0 0 1 .41-1.735 3.7 3.7 0 0 1 1.133-1.35l12.144-9.149a3.708 3.708 0 0 1 2.311-.771 3.735 3.735 0 0 1 2.317.771l12.144 9.149a3.7 3.7 0 0 1 1.133 1.35 3.829 3.829 0 0 1 .41 1.735v18.872a3.078 3.078 0 0 1-.946 2.254 3.077 3.077 0 0 1-2.254.946h-8.41a1.866 1.866 0 0 1-1.374-.554 1.866 1.866 0 0 1-.554-1.374v-10.749h-4.923v10.749a1.866 1.866 0 0 1-.554 1.374 1.866 1.866 0 0 1-1.374.554H183.2a3.077 3.077 0 0 1-2.254-.946 3.078 3.078 0 0 1-.946-2.254zm16-14.359z" transform="translate(-178.001 797.691)"/>
        <path data-name="Rectangle 151" fill=none d="M0 0h36v36H0z"/>
    </g>
  </svg>
  `;

  const attendanceSvgCode = `
  <svg xmlns="http://www.w3.org/2000/svg" class="svgIcon" viewBox="0 0 36 36">
    <defs>
        <clipPath id="glxvpop2na">
            <path data-name="Rectangle 23" style="fill:none" d="M0 0h36v36H0z"/>
        </clipPath>
    </defs>
    <g data-name="Group 117">
        <g data-name="Group 5">
            <g data-name="Group 4" style="clip-path:url(#glxvpop2na)">
                <path data-name="Path 2" d="M32.556 34.01H3.444a1.5 1.5 0 0 1-1.5-1.5V14.09a1.5 1.5 0 0 1 1.5-1.5h29.112a1.5 1.5 0 0 1 1.5 1.5v18.42a1.5 1.5 0 0 1-1.5 1.5m-27.612-3h26.112V15.59H4.944z" style="fill:#fff"/>
                <path data-name="Path 3" d="M32.556 15.59a1.5 1.5 0 0 1-1.5-1.5V7.744H4.944v6.346a1.5 1.5 0 1 1-3 0V6.244a1.5 1.5 0 0 1 1.5-1.5h29.112a1.5 1.5 0 0 1 1.5 1.5v7.846a1.5 1.5 0 0 1-1.5 1.5" style="fill:#fff"/>
                <path data-name="Line 3" transform="translate(10.383 3.49)" style="fill:#fff" d="M0 0v4.806"/>
                <path data-name="Path 4" d="M10.382 9.8a1.5 1.5 0 0 1-1.5-1.5V3.489a1.5 1.5 0 1 1 3 0V8.3a1.5 1.5 0 0 1-1.5 1.5" style="fill:#fff"/>
                <path data-name="Line 4" transform="translate(18.097 3.49)" style="fill:#fff" d="M0 0v4.806"/>
                <path data-name="Path 5" d="M18.1 9.8a1.5 1.5 0 0 1-1.5-1.5V3.489a1.5 1.5 0 1 1 3 0V8.3a1.5 1.5 0 0 1-1.5 1.5" style="fill:#fff"/>
                <path data-name="Line 5" transform="translate(25.617 3.49)" style="fill:#fff" d="M0 0v4.806"/>
                <path data-name="Path 6" d="M25.618 9.8a1.5 1.5 0 0 1-1.5-1.5V3.489a1.5 1.5 0 1 1 3 0V8.3a1.5 1.5 0 0 1-1.5 1.5" style="fill:#fff"/>
                <path data-name="Rectangle 17" transform="translate(8.883 18.69)" style="fill:#fff" d="M0 0h3v3H0z"/>
                <path data-name="Rectangle 18" transform="translate(16.5 18.69)" style="fill:#fff" d="M0 0h3v3H0z"/>
                <path data-name="Rectangle 19" transform="translate(24.117 18.69)" style="fill:#fff" d="M0 0h3v3H0z"/>
                <path data-name="Rectangle 20" transform="translate(8.883 24.857)" style="fill:#fff" d="M0 0h3v3H0z"/>
                <path data-name="Rectangle 21" transform="translate(16.5 24.857)" style="fill:#fff" d="M0 0h3v3H0z"/>
                <path data-name="Rectangle 22" transform="translate(24.117 24.857)" style="fill:#fff" d="M0 0h3v3H0z"/>
            </g>
        </g>
        <path data-name="Rectangle 154" style="fill:none" d="M0 0h36v36H0z"/>
    </g>
  </svg>
  `;

  const logoutSvgCode = `
  <svg xmlns="http://www.w3.org/2000/svg" class="svgIcon" viewBox="0 0 36 36">
    <g data-name="Group 147">
        <path d="M134.222-824v-14.222a1.72 1.72 0 0 1 .511-1.267A1.72 1.72 0 0 1 136-840a1.72 1.72 0 0 1 1.267.511 1.72 1.72 0 0 1 .511 1.267V-824a1.72 1.72 0 0 1-.511 1.267 1.72 1.72 0 0 1-1.267.511 1.72 1.72 0 0 1-1.267-.511 1.72 1.72 0 0 1-.511-1.267zM136-808a15.372 15.372 0 0 1-6.2-1.267 16.343 16.343 0 0 1-5.089-3.444 16.344 16.344 0 0 1-3.444-5.089A15.37 15.37 0 0 1 120-824a15.659 15.659 0 0 1 1.133-5.889 16.432 16.432 0 0 1 3.178-5.044 1.614 1.614 0 0 1 1.244-.533 1.756 1.756 0 0 1 1.289.533 1.519 1.519 0 0 1 .444 1.222 2.188 2.188 0 0 1-.533 1.311 12.616 12.616 0 0 0-2.356 3.844 12.376 12.376 0 0 0-.844 4.556 11.99 11.99 0 0 0 3.644 8.8 11.99 11.99 0 0 0 8.8 3.645 11.969 11.969 0 0 0 8.822-3.645 12.027 12.027 0 0 0 3.622-8.8 12.727 12.727 0 0 0-.844-4.6 12.062 12.062 0 0 0-2.4-3.889 1.875 1.875 0 0 1-.511-1.244 1.558 1.558 0 0 1 .467-1.2 1.756 1.756 0 0 1 1.289-.533 1.614 1.614 0 0 1 1.244.533 15.7 15.7 0 0 1 3.2 5.044A15.933 15.933 0 0 1 152-824a15.37 15.37 0 0 1-1.267 6.2 16.539 16.539 0 0 1-3.422 5.089 16.074 16.074 0 0 1-5.067 3.444A15.481 15.481 0 0 1 136-808z" transform="translate(-118 842)" style="fill:#fff"/>
        <path data-name="Rectangle 160" style="fill:none" d="M0 0h36v36H0z"/>
    </g>
  </svg>
  `;

  return (
    <>
      {/* Mobile Backdrop Overlay */}
      {isOpen && (
        <div
          onClick={onClose}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 998,
          }}
          className="sidebar-backdrop"
        />
      )}

      <div className={`sidebar ${className} ${isOpen ? "sidebar--open" : ""}`}>
        <div
          className="sidebar__logo"
          style={{
            padding: "10px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Link to="/">
            <img
              className="SidebarLogo"
              src="/logo.png"
              alt="logo"
              style={{ paddingTop: "5px" }}
            />
          </Link>
          {/* Close button for mobile */}
          {isOpen && (
            <button
              onClick={onClose}
              style={{
                background: "transparent",
                border: "none",
                color: "white",
                fontSize: "24px",
                cursor: "pointer",
                display: "none", // Controlled via media query CSS
              }}
              className="mobile-close-btn"
            >
              &times;
            </button>
          )}
        </div>
        <div>
          <nav className="main__navbar" onClick={onClose}>
            <SidebarItem to="/">
              <div
                className="main__navbar__icon"
                dangerouslySetInnerHTML={{ __html: dashboardSvgCode }}
              />
              Dashboard
            </SidebarItem>

            {userRoleId !== 1 && (
              <SidebarItem to="/profile">
                <div
                  className="main__navbar__icon"
                  dangerouslySetInnerHTML={{ __html: profileSvgCode }}
                />
                Profile
              </SidebarItem>
            )}

            {(userRoleId === 1 || userRoleId === 4) && (
              <SidebarItem to="/users">
                <div
                  className="main__navbar__icon"
                  dangerouslySetInnerHTML={{ __html: profileSvgCode }}
                />
                Users
              </SidebarItem>
            )}

            <SidebarItem to="/booking">
              <div
                className="main__navbar__icon"
                dangerouslySetInnerHTML={{ __html: attendanceSvgCode }}
              />
              Booking & Payment
            </SidebarItem>

            {userRoleId === 4 && (
              <SidebarItem to="/services">
                <div
                  className="main__navbar__icon"
                  dangerouslySetInnerHTML={{ __html: attendanceSvgCode }}
                />
                Services
              </SidebarItem>
            )}
          </nav>
        </div>

        <div style={{ marginTop: 0 }} className="main__navbar">
          <button
            style={{
              backgroundColor: "#719879",
              border: "none",
              cursor: "pointer",
            }}
            className="navbar__item"
            onClick={() => setIsModalOpen(true)}
          >
            <span className="navbar__item__span">
              <div
                className="main__navbar__icon"
                dangerouslySetInnerHTML={{ __html: logoutSvgCode }}
              />
              Logout
            </span>
          </button>
        </div>
        <RejectionModal isOpen={isModalOpen} onClose={closeModal}>
          <div className="modal__popup">
            <div className="Portal-Login-Form">
              <div
                className="ValidationModal"
                style={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <span className="approvalText">
                  Are you sure you want to logout ?
                </span>

                <div className="CloseRejectionModalButton">
                  <button
                    type="button"
                    className="CloseRejectionModal"
                    onClick={closeModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="rejectButtonValidationModal"
                    onClick={handleLogout}
                  >
                    LogOut
                  </button>
                </div>
              </div>
            </div>
          </div>
        </RejectionModal>
      </div>
    </>
  );
}
